// src/index.tsx

// 0) Inject Adobe Launch container tag at build time
declare const __LAUNCH_SRC__: string;
const launchScript = document.createElement("script");
launchScript.src = __LAUNCH_SRC__;
launchScript.async = true;
document.head.appendChild(launchScript);

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, RouterProvider } from "react-router-dom";
import { TranslationProvider } from "./i18n/TranslationContext";
import "./index.css";

import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";
import { trackLinkClick, trackDownload } from "./analytics";

// 1) Pull lang out of the fragment immediately
const getInitialLang = (): string | undefined => {
    const h = window.location.hash;
    if (h.startsWith("#?")) {
        const params = new URLSearchParams(h.substring(2));
        return params.get("lang")?.toUpperCase() ?? undefined;
    }
    return undefined;
};

// 2) Helpers to capture & restore fragment
function captureAndStripFragment() {
    const h = window.location.hash;
    if (h.startsWith("#?")) {
        sessionStorage.setItem("origFrag", h.substring(2));
        window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }
}

function restoreFragment() {
    const frag = sessionStorage.getItem("origFrag");
    if (frag) {
        window.location.replace(
            window.location.pathname + window.location.search + `#?${frag}`
        );
        sessionStorage.removeItem("origFrag");
    }
}

// 3) AuthHandler now driven by props rather than static imports
interface AuthHandlerProps {
    children: React.ReactNode;
    loginUrl: string;
    isAuthenticated: boolean;
}

const AuthHandler: React.FC<AuthHandlerProps> = ({ children, loginUrl, isAuthenticated }) => {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        captureAndStripFragment();
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            const frag = sessionStorage.getItem("origFrag");
            const url = frag
                ? `${loginUrl}?fragment=${encodeURIComponent(frag)}`
                : loginUrl;
            window.location.href = url;
            return;
        }
        restoreFragment();
        setReady(true);
    }, [isAuthenticated, loginUrl]);

    if (!ready) return null;
    return <>{children}</>;
};

// 4) AppRoot to fetch auth_setup and build router
const AppRoot: React.FC = () => {
    const [initialLang] = useState<string | undefined>(getInitialLang());
    const [authConfig, setAuthConfig] = useState<{
        useLogin: boolean;
        requireAccessControl: boolean;
        loginUrl: string;
        isAuthenticated: boolean;
    } | null>(null);

    useEffect(() => {
        fetch("/auth_setup")
            .then((res) => {
                if (!res.ok) throw new Error("Failed to load auth setup");
                return res.json();
            })
            .then((cfg) => setAuthConfig(cfg))
            .catch((err) => {
                console.error(err);
                setAuthConfig({
                    useLogin: false,
                    requireAccessControl: false,
                    loginUrl: "/login",
                    isAuthenticated: false,
                });
            });
    }, []);

    if (authConfig === null) return null;

    const router = createHashRouter([
        {
            path: "/",
            element: authConfig.useLogin ? (
                <AuthHandler
                    loginUrl={authConfig.loginUrl}
                    isAuthenticated={authConfig.isAuthenticated}
                >
                    <Layout />
                </AuthHandler>
            ) : (
                <Layout />
            ),
            children: [
                { index: true, element: <Chat /> },
                { path: "chat", element: <Chat /> },
                { path: "*", lazy: () => import("./pages/NoPage") },
            ],
        },
    ]);

    return (
        <TranslationProvider initialLang={initialLang}>
            <RouterProvider router={router} />
        </TranslationProvider>
    );
};

// --- Global click handler for citation and text links ---
const GlobalLinkHandler: React.FC = () => {
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const anchor = (e.target as Element).closest(
                ".tooltipWrapper a, a.textLink"
            ) as HTMLAnchorElement | null;
            if (!anchor) return;
            e.preventDefault();

            const quickUrl = anchor.href;
            const lang = (window as any).__APP_LANG__ || "en";
            const label = anchor.dataset.label ?? anchor.textContent ?? "link";
            const opts = {
                pageName: `${lang}:lifenet:ai-search`,
                title: document.title,
                url: window.location.origin + window.location.pathname,
                lang,
                domain: window.location.host,
                fullUrl: window.location.href,
            };

            const m = quickUrl.match(/\.([a-z0-9]+)(?:$|\?)/i);
            const ext = m?.[1]?.toLowerCase();
            if (ext && ext !== "html" && ext !== "pdf") {
                trackDownload(label, quickUrl, opts);
            } else {
                trackLinkClick(label, quickUrl, opts);
            }
            window.open(quickUrl, "_blank", "noopener,noreferrer");
        };

        document.body.addEventListener("click", handler);
        return () => {
            document.body.removeEventListener("click", handler);
        };
    }, []);

    return null;
};

// 5) Bootstrap
ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
).render(
    <React.StrictMode>
        <GlobalLinkHandler />
        <AppRoot />
    </React.StrictMode>
);
